import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from './UserContext';
import apiClient from '../services/apiClient';
import toast from '../utils/toast';

const AgentNotificationContext = createContext();

export const useAgentNotifications = () => {
  const context = useContext(AgentNotificationContext);
  if (!context) {
    throw new Error('useAgentNotifications must be used within an AgentNotificationProvider');
  }
  return context;
};

export const AgentNotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useUser();

  // Module-scoped refs — no window globals
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const baseUrl = '/api/notifications';

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const { data } = await apiClient.get(baseUrl);
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user, baseUrl]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    try {
      const { data } = await apiClient.get(`${baseUrl}/unread-count`);
      setUnreadCount(data.count || data.unreadCount || 0);
    } catch (error) {
      // 401 is expected before auth is complete — don't log it
      if (error.response?.status !== 401) {
        console.error('Error fetching unread count:', error);
      }
      setUnreadCount(0);
    }
  }, [user, baseUrl]);

  const scheduleReconnect = useCallback(() => {
    // Cancel any pending reconnect before scheduling a new one
    clearTimeout(reconnectTimeoutRef.current);
    reconnectTimeoutRef.current = setTimeout(() => {
      if (user) {
        connectToNotificationStream(); // eslint-disable-line no-use-before-define
      }
    }, 5000);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const connectToNotificationStream = useCallback(() => {
    // Close existing connection before opening a new one
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (isConnected || !user) return;

    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      console.error('VITE_API_URL is not set, cannot connect to notification stream');
      return;
    }

    try {
      const eventSource = new EventSource(`${apiUrl}${baseUrl}/stream`, {
        withCredentials: true,
      });

      eventSource.onopen = () => {
        setIsConnected(true);
      };

      eventSource.onerror = () => {
        const readyState = eventSource.readyState;

        if (readyState === EventSource.CLOSED) {
          setIsConnected(false);
          eventSource.close();
          if (eventSourceRef.current === eventSource) {
            eventSourceRef.current = null;
          }
          if (user) scheduleReconnect();
        }
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case 'connection':
              break;

            case 'heartbeat':
              break;

            case 'new_notification':
              setNotifications((prev) => [data.notification, ...prev]);
              setUnreadCount((prev) => prev + 1);
              toast.success(`New notification: ${data.notification.title}`);
              break;

            case 'notification_update':
              if (data.updateType === 'read') {
                setNotifications((prev) =>
                  prev.map((n) =>
                    n.notificationid === data.notificationId ? { ...n, is_read: true } : n
                  )
                );
              } else if (data.updateType === 'deleted') {
                setNotifications((prev) =>
                  prev.filter((n) => n.notificationid !== data.notificationId)
                );
              } else if (data.updateType === 'unread_count') {
                setUnreadCount(data.data.count);
              }
              break;

            default:
              break;
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      console.error('SSE connection error:', error);
      setIsConnected(false);
      if (user) scheduleReconnect();
    }
  }, [user, isConnected, baseUrl, scheduleReconnect]);

  const disconnectFromNotificationStream = useCallback(() => {
    clearTimeout(reconnectTimeoutRef.current);
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await apiClient.patch(`${baseUrl}/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationid === notificationId ? { ...n, is_read: true } : n
        )
      );
      await fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  }, [baseUrl, fetchUnreadCount]);

  const markAllAsRead = useCallback(async () => {
    try {
      await apiClient.patch(`${baseUrl}/read-all`);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      await fetchUnreadCount();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  }, [baseUrl, fetchUnreadCount]);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await apiClient.delete(`${baseUrl}/${notificationId}`);
      setNotifications((prev) => prev.filter((n) => n.notificationid !== notificationId));
      await fetchUnreadCount();
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  }, [baseUrl, fetchUnreadCount]);

  // Connect and fetch when user becomes available
  useEffect(() => {
    if (!user) return;

    fetchNotifications();
    fetchUnreadCount();
    connectToNotificationStream();

    return () => {
      disconnectFromNotificationStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fallback polling when SSE is not connected
  useEffect(() => {
    if (!user || isConnected) return;

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [user, isConnected, fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    loading,
    isConnected,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    connectToNotificationStream,
    disconnectFromNotificationStream,
  };

  return (
    <AgentNotificationContext.Provider value={value}>
      {children}
    </AgentNotificationContext.Provider>
  );
};
